import React, { useEffect, useState, useMemo } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';

interface SaveHighlightModalProps {
  isOpen: boolean;
  initialName: string;
  isSaving: boolean;
  isEditingExisting?: boolean;
  highlightStatus?: 'saved' | 'queued' | 'processing' | 'stitched' | 'enhancing' | 'completed' | 'failed';
  highlight?: {
    segments_data?: any[];
    segment_effects_config?: any;
    overlay_config?: any;
    output_storage_path?: string | null;
    enhanced_storage_path?: string | null;
  };
  currentClips?: any[];
  currentSegmentEffectsConfig?: any;
  currentOverlayConfig?: any;
  onClose: () => void;
  onSave: (name: string, action: 'update' | 'create') => void;
}

const SaveHighlightModal: React.FC<SaveHighlightModalProps> = ({
  isOpen,
  initialName,
  isSaving,
  isEditingExisting = false,
  highlightStatus,
  highlight,
  currentClips,
  currentSegmentEffectsConfig,
  currentOverlayConfig,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState(initialName);
  const [action, setAction] = useState<'update' | 'create'>('create');

  // Detect what changed
  const changes = useMemo(() => {
    if (!highlight || !isEditingExisting) {
      return {
        segmentsChanged: false,
        segmentEffectsChanged: false,
        overlayConfigChanged: false,
      };
    }

    return {
      segmentsChanged: JSON.stringify(highlight.segments_data || []) !== 
                       JSON.stringify(currentClips || []),
      segmentEffectsChanged: JSON.stringify(highlight.segment_effects_config || null) !== 
                             JSON.stringify(currentSegmentEffectsConfig || null),
      overlayConfigChanged: JSON.stringify(highlight.overlay_config || null) !== 
                            JSON.stringify(currentOverlayConfig || null),
    };
  }, [highlight, currentClips, currentSegmentEffectsConfig, currentOverlayConfig, isEditingExisting]);

  // Determine if update existing is allowed
  const canUpdateExisting = useMemo(() => {
    if (!highlight || !isEditingExisting) return false;
    
    const status = highlightStatus;
    const hasVideo = !!highlight.output_storage_path || !!highlight.enhanced_storage_path;
    
    // Rule 1: If not processed yet (saved/failed) → Can update
    if (['saved', 'failed'].includes(status || '')) {
      return true;
    }
    
    // Rule 2: If stitched → Can update ONLY if ONLY overlay_config changed
    // This allows updating overlay config on stitched highlights (creates new overlay job)
    if (hasVideo && status === 'stitched') {
      const onlyOverlaysChanged = 
        !changes.segmentsChanged && 
        !changes.segmentEffectsChanged && 
        changes.overlayConfigChanged;
      
      return onlyOverlaysChanged;
    }
    
    // Rule 3: If completed → CANNOT update (must save as new)
    // This ensures only ONE set of jobs per highlight (retry mechanism requirement)
    // Completed highlights have finished processing, so changes require new highlight
    if (status === 'completed') {
      return false;
    }
    
    return false;
  }, [highlight, highlightStatus, changes, isEditingExisting]);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      // Set default action based on canUpdateExisting
      if (canUpdateExisting) {
        setAction('update');
      } else {
        setAction('create');
      }
    }
  }, [isOpen, initialName, canUpdateExisting]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave(name.trim(), action);
  };

  // Check if update is disabled
  // Block if: not editing existing, no status, processing states, OR change detection says no
  const isUpdateDisabled = !isEditingExisting || 
    !highlightStatus || 
    ['queued', 'processing', 'enhancing'].includes(highlightStatus) ||
    !canUpdateExisting;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Save Highlight" className="max-w-md">
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div>
          <label htmlFor="highlight-name" className="block text-sm font-medium text-gray-700 mb-2">
            Highlight name
          </label>
          <input
            id="highlight-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter highlight name"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={80}
            autoFocus
          />
          <p className="text-xs text-gray-400 mt-1">
            Choose a descriptive name (e.g., &ldquo;First-half goals&rdquo;).
          </p>
        </div>

        {isEditingExisting && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Save option
            </label>
            <div className="space-y-2">
              <label className={`flex items-start p-3 border rounded-md cursor-pointer transition-colors ${
                action === 'update' && !isUpdateDisabled
                  ? 'border-blue-500 bg-blue-50'
                  : action === 'update' && isUpdateDisabled
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="save-action"
                  value="update"
                  checked={action === 'update'}
                  onChange={() => !isUpdateDisabled && setAction('update')}
                  disabled={isUpdateDisabled}
                  className="mt-0.5 mr-3"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Update existing
                  </div>
                  {isUpdateDisabled && (
                    <div className="text-xs text-gray-500 mt-1">
                      {highlightStatus === 'queued' && 'Cannot update highlights in queue'}
                      {highlightStatus === 'processing' && 'Cannot update highlights being processed'}
                      {highlightStatus === 'enhancing' && 'Cannot update highlights being enhanced'}
                      {highlight && highlight.output_storage_path && 
                       !['queued', 'processing', 'enhancing'].includes(highlightStatus || '') &&
                       (changes.segmentsChanged || changes.segmentEffectsChanged) && 
                       'Segments or effects changed - must save as new'}
                    </div>
                  )}
                  {!isUpdateDisabled && highlight && highlight.output_storage_path && (
                    <div className="text-xs text-green-600 mt-1">
                      Only overlay configuration changed. Video will be updated with new overlays.
                    </div>
                  )}
                </div>
              </label>
              
              <label className={`flex items-start p-3 border rounded-md cursor-pointer transition-colors ${
                action === 'create'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="save-action"
                  value="create"
                  checked={action === 'create'}
                  onChange={() => setAction('create')}
                  className="mt-0.5 mr-3"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Create new
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Save as a new highlight
                    {highlight && highlight.output_storage_path && 
                     (changes.segmentsChanged || changes.segmentEffectsChanged) && 
                     ' (required when segments or effects change)'}
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving || !name.trim()}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SaveHighlightModal;

